import { Link } from 'react-router';
import { Image } from '@rocco/components/image';
import type { ArticleTileViewModel } from '@rocco/ui/tile-article/view-models/article';
import {
    TileOverlayWithFollowSelector,
    TileMobileActionsSlot,
} from '~/modules/moodboards/components';
import { buildTileActionInfo } from '~/modules/moodboards/utils/tile-action-info';
import { TextTooltip } from '~/ui-rocco/components/TextTooltip';
import { buildCdnResizeUrl } from '~/utils/image-cdn-resize';

interface EditorsEditArticleTileProps {
    article: ArticleTileViewModel;
}

/**
 * Article tile for the Editors' Edit section.
 * Displays a "FEATURED" badge, author name, title (TWK Ghost font),
 * and 3-line intro text description.
 */
export const EditorsEditArticleTile = ({
    article,
}: EditorsEditArticleTileProps) => {
    const resizedSrc = article.image?.src
        ? buildCdnResizeUrl(article.image.src, {
              width: 440,
              height: 666,
              fit: 'cover',
          })
        : undefined;

    // An article has no supplier to enquire and no site to visit, so the panel
    // carries the three actions that apply to any picture.
    const panelActions = {
        pinId: article.id,
        pinType: 'ArticlePin' as const,
        share: { url: article.link, title: article.title },
        tileInfo: buildTileActionInfo(article),
    };

    return (
        <div className="relative">
            <Link
                to={article.link}
                prefetch="intent"
                className="group flex flex-col"
            >
                {/* Image container */}
                <div className="relative aspect-440/666 w-full overflow-hidden rounded-lg bg-[#e9e8e7]">
                    {article.image?.src && (
                        <Image
                            src={article.image.src}
                            overrideSrc={resizedSrc}
                            alt={article.image.alt || article.title}
                            fill={true}
                            fit="cover"
                            className="transition-transform duration-300 group-hover:scale-105"
                        />
                    )}

                    {/* FEATURED badge */}
                    <div className="absolute top-5 left-5 flex h-6.5 items-center justify-center rounded-full bg-[#f2f2f2] px-4 transition-opacity duration-300 group-hover:opacity-0">
                        <span className="text-[0.625rem] leading-[0.625rem] font-medium tracking-[0.8px] text-[#232323] uppercase">
                            featured
                        </span>
                    </div>

                    <TileOverlayWithFollowSelector {...panelActions} />
                </div>

                {/* Article info */}
                <div className="flex flex-col">
                    {/* Author/Professional name - uppercase, smaller, gray */}
                    <TextTooltip
                        content={article.professional?.name || 'Article'}
                    >
                        <p className="text-uppercase mt-[2.625rem] line-clamp-1 text-[0.6875rem] leading-none font-medium tracking-[0.5px] text-[#232323] uppercase">
                            {article.professional?.name || 'Article'}
                        </p>
                    </TextTooltip>
                    {/* Article title - TWK Ghost font */}
                    <TextTooltip content={article.title}>
                        <h3 className="mt-[1rem] line-clamp-2 min-h-[5.06rem] font-twk-ghost text-[2.2rem] leading-[1.15] font-normal tracking-[-0.703px] text-[#232323]">
                            {article.title}
                        </h3>
                    </TextTooltip>
                </div>

                {/* Intro text / Description - 3 lines with ellipsis */}
                {article.introText && (
                    <p className="mt-[1rem] line-clamp-3 text-[1rem] leading-[1.125] text-[#232323]">
                        {article.introText}
                    </p>
                )}
            </Link>
            {/* Mobile actions live outside the tile link so a tap on the `•••` or
            a sheet-backdrop dismiss can never navigate the tile. */}
            <TileMobileActionsSlot {...panelActions} />
        </div>
    );
};
